<template>
  <DialogRoot
    :open="isOpen || isStorybook"
    :modal="!isStorybook"
    @update:open="closeModal"
  >
    <DialogPortal :disabled="isStorybook">
      <div class="fixed" :class="$style.root">
        <DialogOverlay
          class="fixed inset-0 -z-10 bg-gray-100/50 backdrop-blur-[1px] dark:bg-[color(display-p3_0.067_0.067_0.063_/_0.5)]"
          :class="$style.overlay"
        />

        <DialogContent
          :aria-describedby="undefined"
          :class="[
            $style.content,
            `flex max-h-full flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl dark:border-border dark:bg-sidebar`,
          ]"
        >
          <VisuallyHidden as-child>
            <DialogTitle>Quick search</DialogTitle>
          </VisuallyHidden>

          <ComboboxRoot
            :default-open="isStorybook"
            class="contents"
            ignore-filter
            @update:model-value="act"
          >
            <div class="relative flex items-center">
              <div
                class="ml-3.5 grid w-6 place-items-center text-muted-foreground"
              >
                <ArrowPathIcon
                  v-if="search.isFetching.value"
                  class="size-4 animate-spin"
                />
                <MagnifyingGlassIcon v-else class="size-4" />
              </div>

              <ComboboxInput
                v-model="query"
                class="h-12 grow border-0 bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:ring-0"
                placeholder="Search by email, name, content..."
                type="search"
                autocomplete="off"
              />
            </div>

            <ComboboxContent
              v-if="results.length > 0"
              class="min-h-0 border-t border-border"
            >
              <ComboboxViewport
                class="flex flex-col gap-3 overflow-y-auto px-4 py-3"
              >
                <ComboboxGroup v-for="section in sections" :key="section.title">
                  <ComboboxLabel
                    class="mb-0.5 ml-[-2px] text-[0.8125rem] font-normal leading-5 text-muted-foreground empty:hidden"
                  >
                    {{ section.title }}
                  </ComboboxLabel>

                  <SearchItem
                    v-for="(item, index) in section.items"
                    :key="`${index}-${item.type}-${item.obj.id}`"
                    :item="item"
                    :query="query"
                    :nested="
                      section.title === 'Stripe' && item.type === 'subscriber'
                    "
                  />
                </ComboboxGroup>

                <ComboboxItem
                  :key="query"
                  :value="createViewAllAction(query)"
                  class="-mx-2 flex select-none scroll-m-3 gap-3 rounded-md px-1.5 py-1 text-gray-700 data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-900 dark:text-white dark:data-[highlighted]:bg-muted dark:data-[highlighted]:text-white"
                >
                  <div
                    class="flex shrink-0 items-center justify-center self-start rounded bg-muted p-1"
                  >
                    <EllipsisIcon class="size-4" />
                  </div>
                  <span class="my-0.5 grow text-sm font-medium">
                    View all results &rarr;
                  </span>
                </ComboboxItem>
              </ComboboxViewport>
            </ComboboxContent>
            <ComboboxContent v-else-if="query && !search.isFetching.value">
              <ComboboxViewport
                class="flex flex-col gap-3 overflow-y-auto px-4 py-3"
              >
                <ComboboxGroup>
                  <ComboboxLabel
                    class="mb-2 ml-[-2px] text-[0.8125rem] font-semibold leading-5 text-muted-foreground empty:hidden"
                  >
                    No results found for "{{ query }}"
                  </ComboboxLabel>

                  <SearchItem
                    :key="`${query}`"
                    :item="{
                      type: 'external_url',
                      text: 'Check the documentation',
                      obj: {
                        id: '1',
                        url: 'https://docs.buttondown.com',
                        icon: BookOpenIcon,
                      },
                    }"
                    :query="query" />

                  <SearchItem
                    :key="`${query}`"
                    :item="{
                      type: 'external_url',
                      text: 'Reach out to support',
                      obj: {
                        id: '1',
                        url: 'https://buttondown.com/support',
                        icon: EnvelopeIcon,
                      },
                    }"
                    :query="query" /></ComboboxGroup
              ></ComboboxViewport>
            </ComboboxContent>
          </ComboboxRoot>
        </DialogContent>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<script lang="ts">
export default {
  name: "SearchModal",
};
</script>

<script lang="ts" setup>
import {
  type AcceptableValue,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxViewport,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from "reka-ui";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import BookOpenIcon from "@/icons/heroicons/book-open-micro.svg";
import EllipsisIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";
import EnvelopeIcon from "@/icons/heroicons/envelope-micro.svg";
import MagnifyingGlassIcon from "@/icons/heroicons/magnifying-glass-micro.svg";
import { useSearchQuery } from "@/lib/api/queries/search/list";
import { debouncedRef } from "@/lib/composables/debounced-ref";
import { useKeybinds } from "@/lib/hotkeys";
import { type Action, createViewAllAction } from "@/lib/search";
import { useNewsletter } from "@/store/stores/account";

import SearchItem from "./SearchItem.vue";

const emit = defineEmits(["close", "update:modelValue"]);

const router = useRouter();
const newsletter = useNewsletter();

const isStorybook = computed(() =>
  window.location.pathname.includes("vue-storybook")
);

const props = defineProps<{
  // Just used for storybook. Gross, I know.
  defaultQuery?: string;
}>();

const isOpen = ref(true);
const closeModal = () => {
  isOpen.value = false;
  setTimeout(() => emit("close"), 250);
};

const query = ref(props.defaultQuery || "");
const debouncedQuery = debouncedRef(query, 300, {});
const search = useSearchQuery({
  q: debouncedQuery,
  brief: true,
  pathname: window.location.pathname,
});

const results = computed(() => search.data.value);

const act = (_value: AcceptableValue) => {
  const _action = _value as Action;
  if (_action.type === "route") {
    router.push(_action.route);
  } else if (_action.type === "action") {
    _action.action?.();
  }

  closeModal();
};

const newFeatures = computed(() => {
  return results.value.filter((item) => {
    if (item.type !== "page" || item.feature === undefined) {
      return false;
    }

    return !newsletter.value.enabled_features.includes(item.feature);
  });
});

const pages = computed(() => {
  return results.value.filter((item) => {
    if (item.type !== "page") {
      return false;
    }

    if (item.obj.name.startsWith("/analytics/")) {
      return false;
    }

    return (
      item.feature === undefined ||
      newsletter.value.enabled_features.includes(item.feature)
    );
  });
});

const analytics = computed(() => {
  return results.value.filter((item) => {
    if (item.type !== "page") {
      return false;
    }

    if (!item.obj.name.startsWith("/analytics/")) {
      return false;
    }

    // Feels weird to do this here, but we can't put it in offline search which ATM doesn

    return (
      item.feature === undefined ||
      newsletter.value.enabled_features.includes(item.feature)
    );
  });
});
const actions = computed(() => {
  return results.value.filter((item) => item.type === "special_action");
});

// Subscribers whose stripe_customer_id matches a stripe result, shown nested under the Stripe section
const nestedSubscriberIds = computed(() => {
  const stripeResults = results.value.filter(
    (item) =>
      item.type === "stripe_customer" || item.type === "stripe_subscription"
  );
  const subscriberResults = results.value.filter(
    (item) => item.type === "subscriber" && item.obj.stripe_customer_id
  );

  const ids = new Set<string>();
  for (const stripeResult of stripeResults) {
    const customerIdToMatch =
      stripeResult.type === "stripe_customer"
        ? stripeResult.obj.customer_id
        : stripeResult.obj.customer_id;
    for (const sub of subscriberResults) {
      if (
        sub.type === "subscriber" &&
        sub.obj.stripe_customer_id === customerIdToMatch
      ) {
        ids.add(sub.obj.id);
      }
    }
  }

  return ids;
});

const stuff = computed(() => {
  return results.value.filter(
    (item) =>
      item.type !== "page" &&
      item.type !== "special_action" &&
      item.type !== "stripe_customer" &&
      item.type !== "stripe_subscription" &&
      !(
        item.type === "subscriber" && nestedSubscriberIds.value.has(item.obj.id)
      )
  );
});

const stripe = computed(() => {
  const stripeResults = results.value.filter(
    (item) =>
      item.type === "stripe_customer" || item.type === "stripe_subscription"
  );
  const subscriberResults = results.value.filter(
    (item) =>
      item.type === "subscriber" && nestedSubscriberIds.value.has(item.obj.id)
  );

  const items: typeof results.value = [];
  const addedSubscriberIds = new Set<string>();
  for (const stripeResult of stripeResults) {
    items.push(stripeResult);
    const customerIdToMatch =
      stripeResult.type === "stripe_customer"
        ? stripeResult.obj.customer_id
        : stripeResult.obj.customer_id;
    for (const sub of subscriberResults) {
      if (
        sub.type === "subscriber" &&
        sub.obj.stripe_customer_id === customerIdToMatch &&
        !addedSubscriberIds.has(sub.obj.id)
      ) {
        items.push(sub);
        addedSubscriberIds.add(sub.obj.id);
      }
    }
  }

  return items;
});

const sections = computed(() => {
  return [
    {
      title: "Results",
      items: stuff.value,
    },
    {
      title: "Stripe",
      items: stripe.value,
    },
    {
      title: "Pages",
      items: pages.value,
    },
    {
      title: "Actions",
      items: actions.value,
    },
    {
      title: "Analytics",
      items: analytics.value,
    },
    {
      title: "Explore new features",
      items: newFeatures.value,
    },
  ].filter((section) => section.items.length > 0);
});

useKeybinds(() => {
  if (!isOpen.value) {
    return;
  }

  return {
    Escape: {
      name: `Close dialog`,
      group: `Dialog`,
      // Handled by Radix Vue
      run: null,
    },
  };
});
</script>

<style module>
.root {
  display: grid;
  place-items: start center;
  position: fixed;
  inset: 0;
  z-index: var(--z-dialog);

  & {
    padding: 16px;
  }
  @media (min-width: 640px) {
    padding: 24px;
  }
  @media (min-width: 768px) {
    padding: 80px;
  }
}

.overlay {
  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: fade-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: fade-out 250ms var(--motion-timing-ease);
    }
  }
}

.content {
  width: 100%;
  max-width: 600px;

  @media (prefers-reduced-motion: no-preference) {
    &[data-state="open"] :global {
      animation: pop-in 250ms var(--motion-timing-ease);
    }
    &[data-state="closed"] :global {
      animation: pop-out 250ms var(--motion-timing-ease);
    }
  }
}

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes overlay-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes content-scale-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes content-scale-out {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.96);
  }
}
</style>
